import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  Lock,
  ShieldAlert,
  CheckCircle2,
  Clock,
  Loader2,
  PenTool,
  Type,
  Calendar,
  Hash,
  CheckSquare,
  FileText,
  X,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Check,
  ChevronDown,
  List,
  ArrowRight,
} from "lucide-react";
import { publicEsignApi } from "../services/api";
import { GetBaseURL } from "../components/utils/urlUtils";
import SignatureCanvas from "../components/esign/SignatureCanvas";
import SignatureTyped from "../components/esign/SignatureTyped";

// Set the PDF.js worker at module level
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ─── Types ──────────────────────────────────────────────────────────────────────

interface SigningField {
  type: "signature" | "initials" | "date_signed" | "text" | "checkbox";
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  label: string;
  value?: string;
  globalIndex: number;
  documentIndex: number;
  signerIndex?: number;
  readOnly?: boolean;
}

interface SigningDocument {
  _id: string;
  title: string;
  url: string;
  requiresSignature: boolean;
}

interface SigningData {
  envelope: {
    _id: string;
    subject: string;
    message?: string;
    status: string;
  };
  firm: {
    name: string;
  };
  signer: {
    name: string;
    email: string;
    role: string;
    order: number;
    status: string;
    signedAt?: string;
  };
  documents: SigningDocument[];
  fields: SigningField[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function todayFormatted(): string {
  return new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const FIELD_ICONS: Record<string, React.ElementType> = {
  signature: PenTool,
  initials: Type,
  date_signed: Calendar,
  text: Hash,
  checkbox: CheckSquare,
};

const FIELD_LABELS: Record<string, string> = {
  signature: "Sign here",
  initials: "Initial here",
  date_signed: "Enter date",
  text: "Enter text",
  checkbox: "Check",
};

// ─── Sub-components ─────────────────────────────────────────────────────────────

function HeaderBar() {
  return (
    <header className="sticky top-0 z-30 bg-white shadow-sm border-b border-slate-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <span
          className="text-xl font-bold tracking-tight"
          style={{ color: "#0F5132" }}
        >
          Kavoa
        </span>
        <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full">
          <Lock className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Secure Signing</span>
        </div>
      </div>
    </header>
  );
}

function StatusScreen({
  icon: Icon,
  iconColor,
  iconBg,
  title,
  description,
}: {
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  title: string;
  description: string;
}) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 sm:p-12 max-w-md w-full text-center"
      >
        <div
          className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${iconBg} mb-6`}
        >
          <Icon className={`h-8 w-8 ${iconColor}`} />
        </div>
        <h1 className="text-xl font-bold text-slate-800 mb-2">{title}</h1>
        <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
      </motion.div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4">
      <Loader2
        className="h-10 w-10 animate-spin mb-4"
        style={{ color: "#0F5132" }}
      />
      <p className="text-sm text-slate-500 font-medium">
        Loading secure document...
      </p>
    </div>
  );
}

// ─── Decline Modal ──────────────────────────────────────────────────────────────

function DeclineModal({
  open,
  onClose,
  onDecline,
  declining,
}: {
  open: boolean;
  onClose: () => void;
  onDecline: (reason: string) => void;
  declining: boolean;
}) {
  const [reason, setReason] = useState("");

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
      >
        <div
          className="absolute inset-0 bg-black/40"
          onClick={onClose}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative bg-white rounded-xl shadow-xl border border-slate-200 p-6 max-w-sm w-full"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <h3 className="text-lg font-semibold text-slate-800">
                Decline to Sign
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Are you sure you want to decline? The sender will be notified.
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for declining (optional)"
            rows={3}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-red-400 resize-none mb-4"
          />
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={declining}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onDecline(reason)}
              disabled={declining}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {declining ? "Declining..." : "Decline"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Inline Text Field Modal ────────────────────────────────────────────────────

function InlineTextModal({
  open,
  label,
  onSubmit,
  onClose,
}: {
  open: boolean;
  label: string;
  onSubmit: (value: string) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState("");

  useEffect(() => {
    if (open) setText("");
  }, [open]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
      >
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative bg-white rounded-xl shadow-xl border border-slate-200 p-6 max-w-sm w-full"
        >
          <h3 className="text-lg font-semibold text-slate-800 mb-3">
            {label}
          </h3>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter text..."
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 mb-4"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && text.trim()) onSubmit(text.trim());
            }}
          />
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (text.trim()) onSubmit(text.trim());
              }}
              disabled={!text.trim()}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
              style={{ backgroundColor: "#0F5132" }}
            >
              Confirm
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Signature Capture Modal ────────────────────────────────────────────────────

function SignatureCaptureModal({
  open,
  mode,
  title,
  onSubmit,
  onClose,
}: {
  open: boolean;
  mode: "signature" | "initials";
  title: string;
  onSubmit: (dataUrl: string, signatureMode: "draw" | "type", typedText?: string) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<"draw" | "type">("draw");
  const [drawnDataUrl, setDrawnDataUrl] = useState<string | null>(null);
  const [typedDataUrl, setTypedDataUrl] = useState<string | null>(null);
  const [typedText, setTypedText] = useState("");

  useEffect(() => {
    if (open) {
      setTab("draw");
      setDrawnDataUrl(null);
      setTypedDataUrl(null);
      setTypedText("");
    }
  }, [open]);

  if (!open) return null;

  const canSubmit =
    tab === "draw" ? !!drawnDataUrl : !!typedDataUrl && !!typedText;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4"
      >
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl border border-slate-200 p-5 sm:p-6 w-full sm:max-w-lg max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>

          {/* Tab Toggle */}
          <div className="flex bg-slate-100 rounded-xl p-1 mb-4">
            <button
              onClick={() => setTab("draw")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === "draw"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <PenTool className="h-4 w-4" />
              Draw
            </button>
            <button
              onClick={() => setTab("type")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === "type"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Type className="h-4 w-4" />
              Type
            </button>
          </div>

          {tab === "draw" ? (
            <SignatureCanvas
              onChange={(dataUrl) => setDrawnDataUrl(dataUrl)}
              height={mode === "initials" ? 120 : 200}
            />
          ) : (
            <SignatureTyped
              onChange={(dataUrl, text) => {
                setTypedDataUrl(dataUrl);
                setTypedText(text);
              }}
            />
          )}

          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const dataUrl = tab === "draw" ? drawnDataUrl : typedDataUrl;
                if (dataUrl) {
                  onSubmit(dataUrl, tab, tab === "type" ? typedText : undefined);
                }
              }}
              disabled={!canSubmit}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
              style={{ backgroundColor: "#0F5132" }}
            >
              Apply {mode === "initials" ? "Initials" : "Signature"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Field Overlay (rendered on top of PDF pages) ────────────────────────────────

function FieldOverlay({
  field,
  fieldIndex,
  value,
  onClick,
  envelopeId,
}: {
  field: SigningField;
  fieldIndex: number;
  value: string | boolean | undefined;
  onClick: (index: number) => void;
  envelopeId?: string;
}) {
  const Icon = FIELD_ICONS[field.type] || Hash;
  const label = field.label || FIELD_LABELS[field.type] || field.type;

  // Sender fields come pre-filled and read-only
  const isReadOnly = field.readOnly === true;
  const displayValue = isReadOnly ? field.value : value;
  const isCompleted = displayValue !== undefined && displayValue !== "" && displayValue !== false;

  const isImageField = field.type === "signature" || field.type === "initials";
  const isCheckbox = field.type === "checkbox";

  return (
    <div
      className={`absolute transition-all group ${isReadOnly ? "cursor-default" : "cursor-pointer"}`}
      style={{
        left: `${field.x}%`,
        top: `${field.y}%`,
        width: `${field.width}%`,
        height: `${field.height}%`,
        zIndex: 10,
      }}
      onClick={() => !isReadOnly && onClick(fieldIndex)}
    >
      {isCompleted ? (
        // ─── Completed field ───────────────────────────────────────
        isReadOnly ? (
          // Previously completed field — verified stamp look
          <div className="w-full h-full rounded-sm border border-emerald-600/30 bg-emerald-50/40 flex flex-col items-center justify-center overflow-hidden relative">
            {isImageField && typeof displayValue === "string" ? (
              <img
                src={displayValue}
                alt={field.type}
                className="w-full h-full object-contain p-0.5 opacity-85"
                draggable={false}
              />
            ) : isCheckbox ? (
              <Check className="h-4 w-4 text-emerald-700" />
            ) : (
              <span className="text-xs font-medium truncate px-1.5 text-slate-800">
                {String(displayValue)}
              </span>
            )}
            {/* Verification stamp */}
            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-1 py-px bg-emerald-600/10 border-t border-emerald-600/20">
              <ShieldAlert className="h-2 w-2 text-emerald-700/60" />
              <span className="text-[7px] font-mono tracking-wider text-emerald-700/60 uppercase">
                verified · {field.globalIndex != null
                  ? `${envelopeId?.slice(-4) || "0000"}-${String(field.globalIndex).padStart(2, "0")}`
                  : "sig"}
              </span>
            </div>
          </div>
        ) : (
          // Current signer's completed field
          <div className="w-full h-full rounded border-2 border-emerald-400 bg-emerald-50/60 flex items-center justify-center overflow-hidden">
            {isImageField && typeof displayValue === "string" ? (
              <img
                src={displayValue}
                alt={field.type}
                className="w-full h-full object-contain p-0.5"
                draggable={false}
              />
            ) : isCheckbox ? (
              <Check className="h-4 w-4 text-emerald-600" />
            ) : (
              <span className="text-xs font-medium truncate px-1 text-emerald-800">
                {String(displayValue)}
              </span>
            )}
          </div>
        )
      ) : (
        // ─── Incomplete field ──────────────────────────────────────
        <div className="w-full h-full rounded border-2 border-dashed border-emerald-500 bg-emerald-50/40 flex items-center justify-center gap-1 animate-field-pulse hover:bg-emerald-50/70 hover:border-emerald-600 transition-colors">
          <Icon className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
          <span className="text-[10px] sm:text-xs font-medium text-emerald-700 truncate">
            {FIELD_LABELS[field.type] || label}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Page Selector Dropdown ──────────────────────────────────────────────────────

function PageSelectorDropdown({
  currentPage,
  numPages,
  onPageChange,
  pagesWithFields,
}: {
  currentPage: number;
  numPages: number;
  onPageChange: (page: number) => void;
  pagesWithFields: Set<number>;
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-xs font-medium text-slate-600 tabular-nums"
      >
        <span>
          Page {currentPage} of {numPages}
        </span>
        <ChevronDown
          className={`h-3 w-3 text-slate-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full mt-1 right-0 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-30 overflow-y-auto"
            style={{ maxHeight: 240, minWidth: 130 }}
          >
            {Array.from({ length: numPages }, (_, i) => i + 1).map((page) => {
              const isActive = page === currentPage;
              const hasFields = pagesWithFields.has(page);
              return (
                <button
                  key={page}
                  onClick={() => {
                    onPageChange(page);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-1.5 text-xs transition-colors ${
                    isActive
                      ? "bg-emerald-50 text-emerald-800 font-semibold"
                      : "text-slate-600 hover:bg-slate-50 font-medium"
                  }`}
                >
                  <span>Page {page}</span>
                  {hasFields && (
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: "#198754" }}
                    />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Fields Navigation Panel ─────────────────────────────────────────────────────

function FieldsNavigationPanel({
  fields,
  completedValues,
  activeDocIndex,
  currentPage,
  onNavigate,
}: {
  fields: SigningField[];
  completedValues: Record<number, string | boolean>;
  activeDocIndex: number;
  currentPage: number;
  onNavigate: (documentIndex: number, page: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  // Separate editable and read-only fields
  const editableFieldIndices = fields
    .map((f, i) => ({ f, i }))
    .filter(({ f }) => !f.readOnly);
  const editableCount = editableFieldIndices.length;

  if (fields.length === 0) return null;

  const completedCount = editableFieldIndices.filter(({ i }) => {
    const val = completedValues[i];
    return val !== undefined && val !== "" && val !== false;
  }).length;

  const allComplete = editableCount > 0 && completedCount === editableCount;

  return (
    <>
      {/* Desktop: floating panel on the left side */}
      <div className="hidden md:block fixed left-4 top-1/2 -translate-y-1/2 z-20">
        <AnimatePresence mode="wait">
          {!expanded ? (
            <motion.button
              key="toggle"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              onClick={() => setExpanded(true)}
              className="flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl shadow-lg border border-slate-200 bg-white hover:shadow-xl transition-shadow"
              style={{ minWidth: 44 }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: allComplete ? "#dcfce7" : "#ecfdf5" }}
              >
                {allComplete ? (
                  <Check className="h-4 w-4 text-emerald-600" />
                ) : (
                  <List className="h-4 w-4 text-emerald-700" />
                )}
              </div>
              <span className="text-[10px] font-bold text-slate-600 tabular-nums">
                {completedCount}/{editableCount}
              </span>
            </motion.button>
          ) : (
            <motion.div
              key="panel"
              initial={{ opacity: 0, x: -20, width: 0 }}
              animate={{ opacity: 1, x: 0, width: "auto" }}
              exit={{ opacity: 0, x: -20, width: 0 }}
              className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden"
              style={{ maxHeight: "70vh", width: 220 }}
            >
              {/* Panel header */}
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100 bg-slate-50/50">
                <span className="text-xs font-semibold text-slate-700">
                  Fields to Complete
                </span>
                <button
                  onClick={() => setExpanded(false)}
                  className="p-0.5 hover:bg-slate-200 rounded-md transition-colors"
                >
                  <X className="h-3.5 w-3.5 text-slate-400" />
                </button>
              </div>

              {/* Progress bar */}
              <div className="px-3 py-2 border-b border-slate-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-medium text-slate-500">
                    {completedCount} of {editableCount} complete
                  </span>
                  {allComplete && (
                    <span className="text-[10px] font-medium text-emerald-600">
                      All done
                    </span>
                  )}
                </div>
                <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ backgroundColor: "#0F5132" }}
                    animate={{
                      width: `${editableCount > 0 ? (completedCount / editableCount) * 100 : 100}%`,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              {/* Field list */}
              <div
                className="overflow-y-auto"
                style={{ maxHeight: "calc(70vh - 100px)" }}
              >
                {fields.map((field, i) => {
                  if (field.readOnly) return null;
                  const Icon = FIELD_ICONS[field.type] || Hash;
                  const val = completedValues[i];
                  const isComplete =
                    val !== undefined && val !== "" && val !== false;
                  const isActive =
                    field.documentIndex === activeDocIndex &&
                    field.page === currentPage;
                  const label =
                    field.label || FIELD_LABELS[field.type] || field.type;

                  return (
                    <button
                      key={i}
                      onClick={() =>
                        onNavigate(field.documentIndex, field.page)
                      }
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-b-0 ${
                        isActive ? "bg-emerald-50/50" : ""
                      }`}
                    >
                      <div
                        className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${
                          isComplete
                            ? "bg-emerald-100"
                            : isActive
                            ? "bg-emerald-50 border border-emerald-200"
                            : "bg-slate-100"
                        }`}
                      >
                        {isComplete ? (
                          <Check className="h-3 w-3 text-emerald-600" />
                        ) : (
                          <Icon
                            className={`h-3 w-3 ${
                              isActive
                                ? "text-emerald-600"
                                : "text-slate-400"
                            }`}
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-xs font-medium truncate ${
                            isComplete
                              ? "text-emerald-700"
                              : "text-slate-700"
                          }`}
                        >
                          {label}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          Page {field.page}
                        </p>
                      </div>
                      {!isComplete && (
                        <ArrowRight className="h-3 w-3 text-slate-300 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile: horizontal bar above the document */}
      <div className="md:hidden mb-3">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-3 py-2 bg-white rounded-xl border border-slate-200 shadow-sm"
        >
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center"
              style={{ backgroundColor: allComplete ? "#dcfce7" : "#ecfdf5" }}
            >
              {allComplete ? (
                <Check className="h-3 w-3 text-emerald-600" />
              ) : (
                <List className="h-3 w-3 text-emerald-700" />
              )}
            </div>
            <span className="text-xs font-semibold text-slate-700">
              Fields: {completedCount}/{editableCount}
            </span>
          </div>
          <ChevronDown
            className={`h-4 w-4 text-slate-400 transition-transform ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </button>
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden max-h-48 overflow-y-auto">
                {fields.map((field, i) => {
                  if (field.readOnly) return null;
                  const Icon = FIELD_ICONS[field.type] || Hash;
                  const val = completedValues[i];
                  const isComplete =
                    val !== undefined && val !== "" && val !== false;
                  const label =
                    field.label || FIELD_LABELS[field.type] || field.type;

                  return (
                    <button
                      key={i}
                      onClick={() => {
                        onNavigate(field.documentIndex, field.page);
                        setExpanded(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-b-0"
                    >
                      <div
                        className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${
                          isComplete ? "bg-emerald-100" : "bg-slate-100"
                        }`}
                      >
                        {isComplete ? (
                          <Check className="h-2.5 w-2.5 text-emerald-600" />
                        ) : (
                          <Icon className="h-2.5 w-2.5 text-slate-400" />
                        )}
                      </div>
                      <span
                        className={`text-xs font-medium truncate flex-1 ${
                          isComplete
                            ? "text-emerald-700"
                            : "text-slate-700"
                        }`}
                      >
                        {label}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        p.{field.page}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

// ─── PDF Document Viewer ────────────────────────────────────────────────────────

function PdfDocumentViewer({
  documentUrl,
  currentPage,
  numPages,
  onNumPagesChange,
  fieldsOnPage,
  completedValues,
  onFieldClick,
  envelopeId,
}: {
  documentUrl: string;
  currentPage: number;
  numPages: number;
  onNumPagesChange: (n: number) => void;
  fieldsOnPage: { field: SigningField; fieldIndex: number }[];
  completedValues: Record<number, string | boolean>;
  onFieldClick: (index: number) => void;
  envelopeId?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(true);

  // Memoize the file object to prevent react-pdf worker restarts
  const pdfFile = useMemo(
    () => (documentUrl ? { url: documentUrl } : null),
    [documentUrl]
  );

  // Measure container width via ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateWidth = () => {
      const rect = el.getBoundingClientRect();
      setContainerWidth(rect.width);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const pageWidth = containerWidth > 0 ? Math.min(containerWidth, 800) : 0;

  const handleLoadSuccess = useCallback(
    ({ numPages: n }: { numPages: number }) => {
      setPdfLoading(false);
      onNumPagesChange(n);
    },
    [onNumPagesChange]
  );

  const handleLoadError = useCallback(() => {
    setPdfLoading(false);
  }, []);

  return (
    <div ref={containerRef} className="w-full flex justify-center">
      {pageWidth > 0 && pdfFile && (
        <div className="relative" style={{ width: pageWidth }}>
          {/* PDF rendering */}
          <Document
            file={pdfFile}
            onLoadSuccess={handleLoadSuccess}
            onLoadError={handleLoadError}
            loading={null}
          >
            <Page
              pageNumber={currentPage}
              width={pageWidth}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </Document>

          {/* Loading overlay */}
          {pdfLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80">
              <Loader2 className="h-8 w-8 text-slate-400 animate-spin mb-2" />
              <span className="text-sm text-slate-500">Loading page...</span>
            </div>
          )}

          {/* Field overlays on this page */}
          {!pdfLoading &&
            fieldsOnPage.map(({ field, fieldIndex }) => (
              <FieldOverlay
                key={`field-${fieldIndex}`}
                field={field}
                fieldIndex={fieldIndex}
                value={completedValues[fieldIndex]}
                onClick={onFieldClick}
                envelopeId={envelopeId}
              />
            ))}
        </div>
      )}
    </div>
  );
}

// ─── Pulsing animation CSS (injected once) ──────────────────────────────────────

const pulseStyleId = "sign-doc-pulse-style";

function injectPulseAnimation() {
  if (document.getElementById(pulseStyleId)) return;
  const style = document.createElement("style");
  style.id = pulseStyleId;
  style.textContent = `
    @keyframes fieldPulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
    .animate-field-pulse {
      animation: fieldPulse 2s ease-in-out infinite;
    }
  `;
  document.head.appendChild(style);
}

// ─── Main Component ─────────────────────────────────────────────────────────────

export default function SignDocumentPage() {
  const { token } = useParams<{ token: string }>();

  // Inject CSS animation
  useEffect(() => {
    injectPulseAnimation();
  }, []);

  // Data fetching state
  const [data, setData] = useState<SigningData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signingComplete, setSigningComplete] = useState(false);
  const [allSigned, setAllSigned] = useState(false);
  const [declined, setDeclined] = useState(false);

  // Signing state
  const [completedValues, setCompletedValues] = useState<
    Record<number, string | boolean>
  >({});
  const [reviewedCheckbox, setReviewedCheckbox] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Stored signature/initials data URLs (captured once, reused)
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [signatureMode, setSignatureMode] = useState<"draw" | "type">("draw");
  const [typedSig, setTypedSig] = useState<string>("");
  const [initialsDataUrl, setInitialsDataUrl] = useState<string | null>(null);

  // Modals
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [sigCaptureOpen, setSigCaptureOpen] = useState(false);
  const [sigCaptureMode, setSigCaptureMode] = useState<
    "signature" | "initials"
  >("signature");
  const [textModalOpen, setTextModalOpen] = useState(false);
  const [textModalFieldIndex, setTextModalFieldIndex] = useState<number>(-1);
  const [textModalLabel, setTextModalLabel] = useState("");

  // Document viewer state
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);

  // ─── Fetch signing data ───────────────────────────────────────────────────────

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setError("No signing token provided.");
        setLoading(false);
        return;
      }
      try {
        const res = await publicEsignApi.getSigningData(token);
        setData(res.data.data || res.data);
      } catch (err: any) {
        setError(
          err.response?.data?.error ||
            "This signing link is invalid or has expired."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Reset page when switching documents
  useEffect(() => {
    setCurrentPage(1);
    setNumPages(0);
  }, [activeDocIndex]);

  // Auto-navigate to first field when data loads
  useEffect(() => {
    if (data && data.fields.length > 0) {
      const firstField = data.fields[0];
      if (firstField.documentIndex !== activeDocIndex) {
        setActiveDocIndex(firstField.documentIndex);
      }
      setCurrentPage(firstField.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]); // Only run when data first loads

  // ─── Determine page state ────────────────────────────────────────────────────

  const getPageState = useCallback((): string => {
    if (loading) return "loading";
    if (error) return "error";
    if (declined) return "declined";
    if (signingComplete) return "complete";
    if (!data) return "error";
    if (data.signer.status === "signed") return "already_signed";
    if (data.signer.status === "waiting") return "awaiting_turn";
    if (data.envelope.status === "voided") return "error";
    return "ready";
  }, [loading, error, data, signingComplete, declined]);

  // ─── Fields for active document on current page ────────────────────────────────

  const fieldsOnCurrentPage = useMemo(() => {
    if (!data) return [];
    return data.fields
      .map((field, i) => ({ field, fieldIndex: i }))
      .filter(
        ({ field }) =>
          field.documentIndex === activeDocIndex && field.page === currentPage
      );
  }, [data, activeDocIndex, currentPage]);

  // Pages in the active document that have fields
  const pagesWithFields = useMemo(() => {
    if (!data) return new Set<number>();
    const pages = new Set<number>();
    data.fields.forEach((field) => {
      if (field.documentIndex === activeDocIndex) {
        pages.add(field.page);
      }
    });
    return pages;
  }, [data, activeDocIndex]);

  // ─── Field completion logic ───────────────────────────────────────────────────

  const hasFields = data?.fields && data.fields.length > 0;

  const handleFieldClick = useCallback(
    (index: number) => {
      if (!data) return;
      const field = data.fields[index];

      // If field is on a different page or document, navigate there first
      if (field.documentIndex !== activeDocIndex) {
        setActiveDocIndex(field.documentIndex);
        setCurrentPage(field.page);
        return;
      }
      if (field.page !== currentPage) {
        setCurrentPage(field.page);
        return;
      }

      switch (field.type) {
        case "signature": {
          if (signatureDataUrl) {
            setCompletedValues((prev) => ({
              ...prev,
              [index]: signatureDataUrl,
            }));
          } else {
            setSigCaptureMode("signature");
            setSigCaptureOpen(true);
          }
          break;
        }
        case "initials": {
          if (initialsDataUrl) {
            setCompletedValues((prev) => ({
              ...prev,
              [index]: initialsDataUrl,
            }));
          } else {
            setSigCaptureMode("initials");
            setSigCaptureOpen(true);
          }
          break;
        }
        case "date_signed": {
          setCompletedValues((prev) => ({
            ...prev,
            [index]: todayFormatted(),
          }));
          break;
        }
        case "text": {
          setTextModalFieldIndex(index);
          setTextModalLabel(field.label || "Enter Text");
          setTextModalOpen(true);
          break;
        }
        case "checkbox": {
          setCompletedValues((prev) => ({
            ...prev,
            [index]: !prev[index],
          }));
          break;
        }
        default:
          break;
      }
    },
    [data, signatureDataUrl, initialsDataUrl, activeDocIndex, currentPage]
  );

  const handleSignatureCapture = useCallback(
    (dataUrl: string, mode: "draw" | "type", typed?: string) => {
      if (sigCaptureMode === "signature") {
        setSignatureDataUrl(dataUrl);
        setSignatureMode(mode);
        if (typed) setTypedSig(typed);

        // Auto-fill ALL signature fields
        if (data) {
          const updates: Record<number, string> = {};
          data.fields.forEach((f, i) => {
            if (f.type === "signature") updates[i] = dataUrl;
          });
          setCompletedValues((prev) => ({ ...prev, ...updates }));
        }
      } else {
        setInitialsDataUrl(dataUrl);

        // Auto-fill ALL initials fields
        if (data) {
          const updates: Record<number, string> = {};
          data.fields.forEach((f, i) => {
            if (f.type === "initials") updates[i] = dataUrl;
          });
          setCompletedValues((prev) => ({ ...prev, ...updates }));
        }
      }
      setSigCaptureOpen(false);
    },
    [sigCaptureMode, data]
  );

  const handleTextSubmit = useCallback(
    (value: string) => {
      setCompletedValues((prev) => ({
        ...prev,
        [textModalFieldIndex]: value,
      }));
      setTextModalOpen(false);
    },
    [textModalFieldIndex]
  );

  // ─── Submission ───────────────────────────────────────────────────────────────

  const requiredFieldsComplete = useCallback((): boolean => {
    if (!data) return false;
    if (!hasFields) {
      const hasSignature =
        signatureMode === "draw" ? !!signatureDataUrl : !!typedSig;
      return reviewedCheckbox && hasSignature;
    }

    return data.fields.every((field, i) => {
      if (field.readOnly) return true; // Sender fields are pre-filled
      if (!field.required) return true;
      const val = completedValues[i];
      return val !== undefined && val !== "" && val !== false;
    });
  }, [
    data,
    hasFields,
    completedValues,
    reviewedCheckbox,
    signatureDataUrl,
    signatureMode,
    typedSig,
  ]);

  const editableFields = data
    ? data.fields.filter((f) => !f.readOnly)
    : [];

  const completedCount = data
    ? editableFields.filter((f) => {
        const idx = data.fields.indexOf(f);
        const val = completedValues[idx];
        return val !== undefined && val !== "" && val !== false;
      }).length
    : 0;

  const totalFields = editableFields.length;

  const handleSign = async () => {
    if (!token || !data) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const fieldValues = data.fields
        .map((field, i) => ({
          fieldIndex: field.globalIndex,
          value:
            completedValues[i] !== undefined ? String(completedValues[i]) : "",
          readOnly: field.readOnly,
        }))
        .filter((fv) => !fv.readOnly)
        .map(({ fieldIndex, value }) => ({ fieldIndex, value }));

      const res = await publicEsignApi.submitSignature(token, {
        signatureType: signatureMode,
        signatureDataUrl: signatureDataUrl || undefined,
        typedSignature: typedSig || undefined,
        fieldValues,
      });

      // Capture whether all parties have signed
      const responseData = res?.data?.data || res?.data;
      if (responseData?.allSigned) {
        setAllSigned(true);
      }
      setSigningComplete(true);
    } catch (err: any) {
      setSubmitError(
        err.response?.data?.error ||
          "Failed to submit signature. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async (reason: string) => {
    if (!token) return;
    setDeclining(true);
    try {
      await publicEsignApi.decline(token, reason || undefined);
      setDeclined(true);
      setShowDeclineModal(false);
    } catch (err: any) {
      alert(
        err.response?.data?.error || "Failed to decline. Please try again."
      );
    } finally {
      setDeclining(false);
    }
  };

  // ─── Find next incomplete field for "Next" guidance ───────────────────────────

  const scrollToNextIncomplete = useCallback(() => {
    if (!data) return;
    for (let i = 0; i < data.fields.length; i++) {
      if (data.fields[i].readOnly) continue;
      const val = completedValues[i];
      const isComplete = val !== undefined && val !== "" && val !== false;
      if (!isComplete && data.fields[i].required) {
        const field = data.fields[i];
        if (field.documentIndex !== activeDocIndex) {
          setActiveDocIndex(field.documentIndex);
        }
        setCurrentPage(field.page);
        return;
      }
    }
  }, [data, completedValues, activeDocIndex]);

  // Navigate to a specific field's document and page (used by FieldsNavigationPanel)
  const handleFieldNavigate = useCallback(
    (documentIndex: number, page: number) => {
      if (documentIndex !== activeDocIndex) {
        setActiveDocIndex(documentIndex);
      }
      setCurrentPage(page);
    },
    [activeDocIndex]
  );

  // ─── Render ───────────────────────────────────────────────────────────────────

  const pageState = getPageState();

  // Loading
  if (pageState === "loading") {
    return <LoadingScreen />;
  }

  // Error
  if (pageState === "error") {
    return (
      <StatusScreen
        icon={ShieldAlert}
        iconColor="text-slate-500"
        iconBg="bg-slate-100"
        title="Signing Link Unavailable"
        description={
          error ||
          "This signing link is invalid or has expired. Contact the sender if you believe this is an error."
        }
      />
    );
  }

  // Already Signed
  if (pageState === "already_signed") {
    return (
      <StatusScreen
        icon={CheckCircle2}
        iconColor="text-emerald-600"
        iconBg="bg-emerald-100"
        title="Already Signed"
        description={`You have already signed this document${
          data?.signer.signedAt
            ? ` on ${formatDate(data.signer.signedAt)}`
            : ""
        }.`}
      />
    );
  }

  // Awaiting Turn
  if (pageState === "awaiting_turn") {
    return (
      <StatusScreen
        icon={Clock}
        iconColor="text-amber-600"
        iconBg="bg-amber-100"
        title="Waiting for Other Signers"
        description="Other signers must complete their signatures before you. You will be notified by email when it is your turn."
      />
    );
  }

  // Declined
  if (pageState === "declined") {
    return (
      <StatusScreen
        icon={X}
        iconColor="text-red-500"
        iconBg="bg-red-100"
        title="Signing Declined"
        description="You have declined to sign this document. The sender has been notified."
      />
    );
  }

  // Signing Complete
  if (pageState === "complete") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 sm:p-12 max-w-md w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              delay: 0.2,
            }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-6"
          >
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          </motion.div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">
            {allSigned ? "Signing Complete" : "Your Signature Has Been Recorded"}
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed mb-6">
            {allSigned
              ? "All parties have signed. You will receive a copy of the executed document shortly."
              : "You will receive a copy of the signed document once all remaining parties have signed."}
          </p>
          <p className="text-xs text-slate-400">You may close this window.</p>
        </motion.div>
      </div>
    );
  }

  // ─── Ready to Sign (main flow) ───────────────────────────────────────────────

  if (!data) return null;

  const activeDocument = data.documents[activeDocIndex];
  const isAllComplete = requiredFieldsComplete();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col pb-28">
      <HeaderBar />

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Envelope Info */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 sm:p-6 mb-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">
                From
              </p>
              <p className="text-sm font-semibold text-slate-800">
                {data.firm.name}
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500 bg-slate-50 px-3 py-1.5 rounded-full">
              <PenTool className="h-3.5 w-3.5" />
              Signing as: {data.signer.name}
              {data.signer.role && (
                <span className="text-slate-400 capitalize">
                  ({data.signer.role})
                </span>
              )}
            </div>
          </div>

          <div className="mt-4">
            <h2 className="text-lg font-bold text-slate-800">
              {data.envelope.subject}
            </h2>
          </div>

          {data.envelope.message && (
            <div className="mt-3 bg-slate-50 rounded-xl px-4 py-3 border-l-4 border-slate-300">
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                {data.envelope.message}
              </p>
            </div>
          )}
        </motion.div>

        {/* Fields Navigation Panel */}
        {hasFields && (
          <FieldsNavigationPanel
            fields={data.fields}
            completedValues={completedValues}
            activeDocIndex={activeDocIndex}
            currentPage={currentPage}
            onNavigate={handleFieldNavigate}
          />
        )}

        {/* Document Viewer Section */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-6">
            {/* Viewer header: doc tabs + page navigation */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex-wrap gap-2">
              {/* Document tabs */}
              <div className="flex items-center gap-2 overflow-x-auto">
                {data.documents.length > 1 ? (
                  data.documents.map((doc, i) => (
                    <button
                      key={doc._id}
                      onClick={() => setActiveDocIndex(i)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                        activeDocIndex === i
                          ? "bg-white border border-slate-200 text-slate-800 shadow-sm"
                          : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
                      }`}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      {doc.title}
                    </button>
                  ))
                ) : (
                  <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                    <FileText className="h-4 w-4 text-slate-400" />
                    {activeDocument?.title}
                  </div>
                )}
              </div>

              {/* Page navigation */}
              {numPages > 0 && (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.max(1, p - 1))
                    }
                    disabled={currentPage <= 1}
                    className="p-1 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4 text-slate-600" />
                  </button>
                  <PageSelectorDropdown
                    currentPage={currentPage}
                    numPages={numPages}
                    onPageChange={setCurrentPage}
                    pagesWithFields={pagesWithFields}
                  />
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(numPages, p + 1))
                    }
                    disabled={currentPage >= numPages}
                    className="p-1 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-4 w-4 text-slate-600" />
                  </button>
                </div>
              )}
            </div>

            {/* PDF + fields overlay area */}
            <div className="bg-slate-100 p-4 sm:p-6 flex justify-center min-h-[400px]">
              {activeDocument ? (
                <PdfDocumentViewer
                  documentUrl={`${GetBaseURL()}/sign/${token}/document/${activeDocIndex}`}
                  currentPage={currentPage}
                  numPages={numPages}
                  onNumPagesChange={setNumPages}
                  fieldsOnPage={fieldsOnCurrentPage}
                  completedValues={completedValues}
                  onFieldClick={handleFieldClick}
                  envelopeId={data.envelope._id}
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-slate-400 py-20">
                  <FileText className="h-12 w-12 mb-3" />
                  <p className="text-sm">No document available</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* No fields -- simple review checkbox + signature */}
        {!hasFields && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 sm:p-6 mb-6"
          >
            <label className="flex items-start gap-3 cursor-pointer group mb-6">
              <input
                type="checkbox"
                checked={reviewedCheckbox}
                onChange={(e) => setReviewedCheckbox(e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-slate-600 leading-relaxed group-hover:text-slate-800 transition-colors">
                I have reviewed the document(s) above and agree to sign.
              </span>
            </label>

            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              Your Signature
            </h3>

            {/* Tab toggle */}
            <div className="flex bg-slate-100 rounded-xl p-1 mb-4">
              <button
                onClick={() => setSignatureMode("draw")}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  signatureMode === "draw"
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <PenTool className="h-4 w-4" />
                Draw
              </button>
              <button
                onClick={() => setSignatureMode("type")}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  signatureMode === "type"
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Type className="h-4 w-4" />
                Type
              </button>
            </div>

            {signatureMode === "draw" ? (
              <SignatureCanvas
                onChange={(dataUrl) => setSignatureDataUrl(dataUrl)}
              />
            ) : (
              <SignatureTyped
                onChange={(dataUrl, text) => {
                  setSignatureDataUrl(dataUrl);
                  setTypedSig(text);
                }}
              />
            )}
          </motion.div>
        )}

        {/* Submit error */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {submitError}
          </div>
        )}
      </main>

      {/* ─── Sticky Bottom Action Bar ───────────────────────────────────────────── */}
      <motion.div
        initial={{ y: 80 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30, delay: 0.3 }}
        className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          {/* Progress + actions row */}
          <div className="flex items-center justify-between gap-4">
            {/* Left: progress */}
            <div className="flex items-center gap-3 min-w-0">
              {hasFields ? (
                <>
                  {/* Progress dots for small field counts, bar for larger */}
                  {totalFields <= 8 ? (
                    <div className="flex items-center gap-1.5">
                      {data.fields.map((_, i) => {
                        const val = completedValues[i];
                        const done =
                          val !== undefined && val !== "" && val !== false;
                        return (
                          <div
                            key={i}
                            className={`w-2.5 h-2.5 rounded-full transition-colors ${
                              done ? "bg-emerald-500" : "bg-slate-200"
                            }`}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ backgroundColor: "#0F5132" }}
                        animate={{
                          width:
                            totalFields > 0
                              ? `${(completedCount / totalFields) * 100}%`
                              : "0%",
                        }}
                        transition={{ duration: 0.4 }}
                      />
                    </div>
                  )}
                  <span className="text-xs font-medium text-slate-500 whitespace-nowrap">
                    {completedCount}/{totalFields} fields
                  </span>
                  {completedCount < totalFields && completedCount > 0 && (
                    <button
                      onClick={scrollToNextIncomplete}
                      className="text-xs font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap transition-colors"
                    >
                      Next field
                    </button>
                  )}
                </>
              ) : (
                <span className="text-xs text-slate-500">
                  Review and sign the document above
                </span>
              )}
            </div>

            {/* Right: sign button */}
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={handleSign}
                disabled={!isAllComplete || submitting}
                className="px-6 py-2.5 text-sm font-bold text-white rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                style={{ backgroundColor: "#0F5132" }}
                onMouseEnter={(e) => {
                  if (!submitting && isAllComplete) {
                    e.currentTarget.style.backgroundColor = "#146C43";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#0F5132";
                }}
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  "Complete Signing"
                )}
              </button>
              <button
                onClick={() => setShowDeclineModal(true)}
                className="text-xs text-slate-400 hover:text-red-500 transition-colors"
              >
                Decline to Sign
              </button>
            </div>
          </div>

          {/* Legal disclosure */}
          <p className="text-[10px] text-slate-400 leading-relaxed mt-2">
            By clicking "Complete Signing", I agree that my electronic signature
            is the legal equivalent of my handwritten signature and that I have
            reviewed all documents presented above.
          </p>
        </div>
      </motion.div>

      {/* ─── Modals ─────────────────────────────────────────────────────────────── */}
      <DeclineModal
        open={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        onDecline={handleDecline}
        declining={declining}
      />

      <SignatureCaptureModal
        open={sigCaptureOpen}
        mode={sigCaptureMode}
        title={
          sigCaptureMode === "initials"
            ? "Add Your Initials"
            : "Add Your Signature"
        }
        onSubmit={handleSignatureCapture}
        onClose={() => setSigCaptureOpen(false)}
      />

      <InlineTextModal
        open={textModalOpen}
        label={textModalLabel}
        onSubmit={handleTextSubmit}
        onClose={() => setTextModalOpen(false)}
      />
    </div>
  );
}
